import React, { useEffect, useMemo, useState } from "react"

import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import Image from "next/image"

import InfoIcon from "@/assets/icons/base64/InfoIcon"
import FireBgOrangeIcon from "@/assets/icons/fire_bg_orange.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { rateLimiterAtom } from "@/atoms/pools"
import { SelectedReserveType } from "@/atoms/shared"
import { walletAssetsAtom } from "@/atoms/wallet"
import Button from "@/components/base/Button"
import { RelendTable } from "@/components/relendTable"
import { RelendTooltip } from "@/components/relendTooltip"
import useMobileDevice from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import { borrowConfigs } from "@/utils/actions/borrow"
import { BORROW, PI_RESERVE_ITEM, RE_NGN, RE_VND, REPAY } from "@/utils/constants"
import { tokenDecimalDigits, TokenSymbol } from "@/utils/helpers"
import { RplToken } from "@/utils/modals/RplToken"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import { isSymbolWhitelisted, WHITELIST_SYMBOL_BORROW_RESERVES } from "@/utils/whitelists"
import { Alert, Box, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { ReserveType, USDC, USDT, VND } from "@renec-foundation/relend-adapter"

import OpenIcon from "@/assets/icons/open.svg"
import { networkAtom } from "@/atoms/settings"
import useWalletConnect from "@/hooks/useWalletConnect"
import { getTokenLogoDisplay, LOGO_SIZES } from "@/utils/logo"
import { Info } from "@mui/icons-material"
import Link from "next/link"
import { Stat } from "../../../stat"
import { getLinkExplorerAddress, getSupplyBorrowSortingList } from "../../commons"
import * as styles from "../styles"

const BorrowView = ({
  reserves,
  handleOpenBorrowRepayPopup,
}: {
  reserves: SelectedReserveType[]
  handleOpenBorrowRepayPopup: (item: ReserveType, type: typeof BORROW | typeof REPAY) => void
}) => {
  const trans = useTrans()
  const isMobileDevice = useMobileDevice()

  const [hasMounted, setHasMounted] = useState(false)

  const { connected, isSolanaChain } = useWalletConnect()

  // atoms
  const [walletAssets] = useAtom(walletAssetsAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [rateLimiter] = useAtom(rateLimiterAtom)
  const network = useAtomValue(networkAtom)

  const borrowHeaders = useMemo(
    () => (
      <>
        <TableCell>{trans.dashboard.table.asset}</TableCell>
        <TableCell>{trans.dashboard.table.your_borrow_limit}</TableCell>
        <TableCell>{trans.dashboard.table.borrow_apr}</TableCell>
        <TableCell>
          <Stack direction='row' alignItems='center' gap={1}>
            {trans.dashboard.table.ltv}
            <RelendTooltip title={trans.dashboard.table.ltv_info} arrow>
              <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
            </RelendTooltip>
          </Stack>
        </TableCell>
        <TableCell>{trans.dashboard.table.actions}</TableCell>
      </>
    ),
    [trans],
  )

  const desiredSymbolSortingOrder = useMemo(() => getSupplyBorrowSortingList(network), [network])

  const doesHaveCollateral = useMemo(
    () => selectedObligation && selectedObligation?.deposits.length > 0,
    [selectedObligation],
  )
  const alertNoObligationDisplay =
    !doesHaveCollateral && connected ? (
      <Alert
        severity='info'
        className='w-full mb-2'
        icon={<Info className='text-lg mr-0' />}
        sx={{
          ".MuiAlert-icon": {
            marginRight: 1,
          },
        }}
      >
        {trans.dashboard.table.borrow_no_obligation}
      </Alert>
    ) : null

  const borrowItems = reserves
    ?.sort((a, b) => {
      const indexA = desiredSymbolSortingOrder.indexOf(a.symbol)
      const indexB = desiredSymbolSortingOrder.indexOf(b.symbol)

      // If the token is not in the desiredSymbolSortingOrder, move it to the end by assigning a high index value
      const maxIndex = desiredSymbolSortingOrder.length
      return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB)
    })
    .map((item, index) => {
      const isGrowthHacking = [PI_RESERVE_ITEM.address].includes(item.address)
      const symbol = item.symbol
      const isUSDT = symbol === USDT
      const isUSDC = symbol === USDC

      if (!isSymbolWhitelisted(item.symbol, WHITELIST_SYMBOL_BORROW_RESERVES)) {
        return <React.Fragment key={`${item.symbol}-borrow-${index}`}></React.Fragment>
      }
      const borrowMax = borrowConfigs.calculateMax(item, walletAssets, selectedObligation, rateLimiter)
      const isFiatToken = item.symbol === RE_VND || item.symbol === RE_NGN
      let borrowMaxText = !borrowMax.isZero()
        ? formatToken(
            borrowMax,
            tokenDecimalDigits[item?.symbol as TokenSymbol],
            false,
            false,
            isFiatToken ? BigNumber.ROUND_UP : undefined,
            false,
            styles.boldLeftCell,
          )
        : "-"
      let borrowAPYFormatted = formatPercent(
        item.borrowAPRInterest,
        false,
        2,
        0.01 / 100,
        true,
        styles.greenBoldCenterCell,
      )
      let ltv = `${item.loanToValueRatio * 100}%`

      let marketPrice = formatUsd(item.minPrice)
      if (isFiatToken) {
        marketPrice = formatUsd(item.minPrice, 6)
      }

      const reserve = reserves.find((r) => r.symbol === item.symbol)

      const iconName = reserve ? RplToken.getTokenDisplayName(reserve.symbol, reserve.name ?? "") : null
      const isReNGN = item.symbol === RE_NGN

      if (isGrowthHacking) {
        borrowMaxText = "-"
        borrowAPYFormatted = <>-</>
        ltv = "-"
        marketPrice = "-"
      }

      const explorerMintURL = getLinkExplorerAddress(network, reserve?.mintAddress ?? "")
      const tokenNameDisplay = (
        <Link
          href={explorerMintURL}
          target='_blank'
          className='flex gap-1 items-center no-underline'
          data-testid={`supply-borrow-borrow-token-name-${symbol}`}
        >
          <Typography className='text-neutral-900 text-sm'>{iconName}</Typography>
          <Image className='grayscale' src={OpenIcon} alt='click-icon' width={12} height={12} />
          {item.symbol === RE_VND || item.symbol === VND ? (
            <Link
              href={"https://renec.org/blog/guide-to-borrow-repay-revnd"}
              target='_blank'
              className='flex gap-1 items-center no-underline'
              data-testid={`supply-borrow-borrow-revnd-how-to-link`}
            >
              <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
            </Link>
          ) : null}
        </Link>
      )

      const borrowAPYDisplay =
        (isUSDT || isUSDC) && isSolanaChain ? (
          <Stack
            className='w-fit rounded-md border border-orange-highlight justify-center'
            direction='row'
            alignItems='center'
          >
            <Box className='px-2 text-green-600 font-bold text-sm'>{borrowAPYFormatted}</Box>
            <Image
              className='text-white rounded-tr-md rounded-br-md'
              src={FireBgOrangeIcon}
              alt='highlight-icon'
              width={24}
              height={24}
            />
          </Stack>
        ) : (
          <Typography className='text-green-600 font-bold text-sm'>{borrowAPYFormatted}</Typography>
        )

      if (isMobileDevice) {
        return (
          <Stack
            key={`${item.symbol}-borrow-${index}`}
            className={`${index > 0 ? "border-t border-solid border-secondary pt-6 mt-6" : ""}`}
            gap={1}
          >
            <Box sx={styles.iconWrapper}>
              {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.MD)}
              {tokenNameDisplay}
            </Box>
            <Box>
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.your_borrow_limit}</Typography>}
                content={borrowMaxText}
                contentStyle={styles.boldCenterCell}
              />
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.borrow_apr}</Typography>}
                content={
                  <Box sx={styles.flexWrapper}>
                    {borrowAPYDisplay}
                    {isReNGN ? (
                      <RelendTooltip title={trans.dashboard.table.temp_ngn_borrow_apr_info} arrow>
                        <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                      </RelendTooltip>
                    ) : null}
                  </Box>
                }
                contentStyle={styles.greenBoldCenterCell}
              />
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={
                  <Box sx={{ ...styles.ltvWrapper, ...styles.statTitleGray }}>
                    {trans.dashboard.table.ltv}
                    <RelendTooltip title={trans.dashboard.table.ltv_info} arrow>
                      <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                    </RelendTooltip>
                  </Box>
                }
                content={ltv}
                contentStyle={styles.semiBoldCenterCell}
              />
            </Box>
            <Button
              variant='primary'
              onClick={() => {
                handleOpenBorrowRepayPopup(item, BORROW)
              }}
              size='md'
            >
              {trans.dashboard.table.borrow}
            </Button>
          </Stack>
        )
      }

      return (
        <TableRow sx={styles.tableBodyRow} key={`${item.symbol}-borrow-${index}`}>
          <TableCell>
            <Stack direction='row' gap={1} alignItems='start'>
              {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.MD)}
              <Stack gap={0}>
                {tokenNameDisplay}
                <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
              </Stack>
            </Stack>
          </TableCell>
          <TableCell data-testid={`${item.symbol}-borrow-max`}>
            <Typography className='font-semibold text-neutral-900 text-sm'>{borrowMaxText}</Typography>
          </TableCell>
          <TableCell>
            <Stack direction='row' gap={1} alignItems='center'>
              {borrowAPYDisplay}
              {isReNGN && (
                <RelendTooltip title={trans.dashboard.table.temp_ngn_borrow_apr_info} arrow>
                  <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                </RelendTooltip>
              )}
            </Stack>
          </TableCell>
          <TableCell data-testid={`${item.symbol}-borrow-ltv`}>
            <Typography className='font-semibold text-neutral-900 text-sm'>{ltv}</Typography>
          </TableCell>
          <TableCell>
            <Box sx={styles.actionsWrapper}>
              <Button
                name={`${item.symbol}-borrow`}
                variant='primary'
                onClick={() => {
                  handleOpenBorrowRepayPopup(item, BORROW)
                }}
                size='md'
                className='min-w-full'
              >
                {trans.dashboard.table.borrow}
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      )
    })

  const isBorrowEmpty = !borrowItems || (borrowItems && borrowItems.length === 0)

  // This is for the tabbing. This component will be rendered on client-side
  // Will be added for skeleton layout in the future.
  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!hasMounted) {
    return <></>
  }

  return (
    <Stack data-testid='overview-borrow-table-view'>
      {!isMobileDevice && (
        <RelendTable
          loading={isBorrowEmpty}
          headers={borrowHeaders}
          items={borrowItems}
          alert={alertNoObligationDisplay}
        />
      )}

      {isMobileDevice && (
        <>
          {alertNoObligationDisplay}
          {borrowItems}
        </>
      )}
    </Stack>
  )
}

export default BorrowView
